import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { DashboardTask } from 'modules/domain/dashboard/types'
import { refetchFunc } from 'modules/domain/common/hooks'
import { DealSource } from 'modules/domain/deal/types'
import { formatDistance } from 'modules/utils/numbers/formatDistance'
import { formatPriceLogistics } from 'modules/utils/numbers/formatPriceLogistics'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import styled from 'styled-components'
import { DealCardPrice } from 'views/components/DealCard/DealCardPrice'
import { DeliveryPeriodData } from 'views/components/DealCard/DeliveryPeriod'
import { DefaultTooltip } from 'views/components/DefaultTooltip/DefaultTooltip'
import { KeyEntityIcon, UserHasDealsIcon } from 'views/components/TooltipIcons/TooltipIcons'
import { TruncatedValue } from 'views/components/TruncatedValue/TruncatedValue'
import DealRoutes from 'views/pages/Deal/routes'
import UserRoutes from 'views/pages/User/routes'
import { EntityWithIconsWrapper } from 'views/styled/common'
import { TextCard } from 'views/styled/ItemCardStyles'
import { CardHeader } from '../CardHeader/CardHeader'
import { AItemLink, Item, ItemBody } from '../styled'
import { CommonInfo, CompanyLink } from './styled'
import formatNumber from 'modules/utils/numbers/formatNumber'

const LinkWithIconsWrapper = styled(EntityWithIconsWrapper)`
  position: relative;
  z-index: 2;
`

type Props = {
  task: DashboardTask
  refetch: refetchFunc
}

export const TaskDealItem: FC<Props> = ({ task, ...props }) => {
  const deal = task.linked_deal
  const { t } = useTranslation('dashboard')
  const { pathname, search } = window.location
  const saleBidOwnerId = deal?.sale_bid.owner.id
  const purchaseBidOwnerId = deal?.purchase_bid.owner.id

  if (!deal) return null

  return (
    <Item>
      <AItemLink
        eventName="deal"
        itemId={deal.id}
        state={{ pathname, search }}
        to={generatePath(DealRoutes.Details, { id: deal.id })}
      />

      <CardHeader task={task} deal={deal} {...props} />

      <ItemBody>
        <DealCardPrice deal={deal} />
        <DefaultTooltip tooltipContent={deal.sale_bid.address.address}>
          <LinkWithIconsWrapper>
            <TruncatedValue hint={deal.sale_bid.company.name || t('company:list.emptyCompanyName')}>
              <CompanyLink
                eventName="saleBidCompany"
                itemId={saleBidOwnerId}
                to={generatePath(UserRoutes.Details, { id: saleBidOwnerId })}
              >
                {deal.sale_bid.company.name || t('company:list.emptyCompanyName')}
              </CompanyLink>
            </TruncatedValue>
            <KeyEntityIcon isVisible={deal.sale_bid.owner.is_significant} />
            <UserHasDealsIcon isVisible={deal.sale_bid.owner.has_shipped_deals} />
          </LinkWithIconsWrapper>
        </DefaultTooltip>
        <DefaultTooltip tooltipContent={deal.purchase_bid.address.address}>
          <LinkWithIconsWrapper>
            <TruncatedValue hint={deal.purchase_bid.company.name || t('company:list.emptyCompanyName')}>
              <CompanyLink
                eventName="purchaseBidCompany"
                itemId={purchaseBidOwnerId}
                to={generatePath(UserRoutes.Details, { id: purchaseBidOwnerId })}
              >
                {deal.purchase_bid.company.name || t('company:list.emptyCompanyName')}
              </CompanyLink>
            </TruncatedValue>
            <KeyEntityIcon isVisible={deal.purchase_bid.owner.is_significant} />
            <UserHasDealsIcon isVisible={deal.purchase_bid.owner.has_shipped_deals} />
          </LinkWithIconsWrapper>
        </DefaultTooltip>

        <CommonInfo>
          <TextCard>
            {deal.product.title}, {deal.season && deal.season.name}
          </TextCard>
          <TextCard>
            {formatQuantity(deal.quantity)}, {formatDistance(deal.distance)},{' '}
            {formatPriceLogistics(deal.price_logistics)}
          </TextCard>
        </CommonInfo>
        <DeliveryPeriodData data={deal} />
        {deal?.source !== DealSource.matching && (
          <TextCard thumbnail>
            {t('margin')} {formatNumber(deal.margin_percent)}%
          </TextCard>
        )}
      </ItemBody>
    </Item>
  )
}
